import { isNil } from "lodash";
import { useEffect } from "react";

import { NO_DATA } from "./constants";

/**
 * This hook handles automatic tab display management.
 * If the currently selected tab is stable (not fetching) and does not contain any data,
 * we should switch to a stable one with data.
 *
 * @param tables Object containing state and data for each table
 * @param tableDisplayed Current table displayed
 * @param setTableDisplayed Setter to change the currently displayed table
 */
export const useTabsManagement = (
  tables,
  tableDisplayed,
  setTableDisplayed
) => {
  useEffect(() => {
    // First off, don't run any of this logic if there is no table or selection data
    // currently valid. Also don't run it if the tab is in the midst of loading, or
    // it has data, because we only want to run this when the current tab is in a
    // stable state and has no data.
    if (
      isNil(tables) ||
      isNil(tableDisplayed) ||
      (tableDisplayed !== NO_DATA && !tables[tableDisplayed].isEmptyInactive)
    ) {
      return;
    }

    // Go through all the tabs and find those that have some data and are not loading
    // i.e. they are in stable state and we could switch to them
    const tableKeys = Object.keys(tables);
    const stableDataTables = tableKeys.filter(
      (currentTable) =>
        tables[currentTable].hasData && !tables[currentTable].isLoading
    );

    // If at least one exists, switch to it
    if (stableDataTables.length >= 1) {
      setTableDisplayed(stableDataTables[0]);
    } else {
      // If we didn't find any tables, then check if we are in a situation
      // where all of the tables are in stable state without data,
      // in which case we should show an indication of no data
      const stableEmptyTables = tableKeys.filter(
        (currentTable) =>
          !tables[currentTable].hasData && !tables[currentTable].isLoading
      );
      if (tableKeys.length === stableEmptyTables.length) {
        setTableDisplayed(NO_DATA);
      }
    }
  }, [tables, tableDisplayed, setTableDisplayed]);
};
